import { useState, useEffect } from 'react';
import { useKanbanAcoes } from './kanban-acoes-context';
import { useKanbanComandos } from './use-kanban-comandos';
import { apiFetch } from '../../../lib/csrf-fetch';
import { validarMotivo, montarCorpoBloqueio } from '../../../lib/kanban/avisos';
import type { KanbanMotivo } from '@pieltec/contracts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Loader2 } from 'lucide-react';

interface KanbanBloqueioDialogProps {
  osId: string;
  versao: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function KanbanBloqueioDialog({ osId, versao, onClose, onSuccess }: KanbanBloqueioDialogProps) {
  const { bloquear } = useKanbanComandos();
  const { atualizar } = useKanbanAcoes();
  const [motivos, setMotivos] = useState<KanbanMotivo[]>([]);
  const [carregandoMotivos, setCarregandoMotivos] = useState(true);
  
  const [tipoInterrupcaoId, setTipoInterrupcaoId] = useState<string>('');
  const [outro, setOutro] = useState('');
  const [loadingBloquear, setLoadingBloquear] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    async function carregarMotivos() {
      try {
        const dados = await apiFetch<KanbanMotivo[]>('/api/v1/kanban/motivos');
        setMotivos(dados);
      } catch {
        // ignora, fica vazio
      } finally {
        setCarregandoMotivos(false);
      }
    }
    void carregarMotivos();
  }, []);

  const handleBloquear = async () => {
    setErro(null);
    const validacao = validarMotivo({ tipoInterrupcaoId, outro });
    if (validacao) {
      setErro(validacao);
      return;
    }

    setLoadingBloquear(true);
    const corpo = montarCorpoBloqueio({ tipoInterrupcaoId, outro }, versao);
    const resposta = await bloquear(osId, corpo);

    if (resposta.tipo === 'ok') {
      onSuccess();
    } else {
      setErro(resposta.message);
      setLoadingBloquear(false);
      // 409 de versão: o cartão mudou; recarrega para o próximo gesto usar a versão vigente.
      if (resposta.tipo === 'conflito') atualizar();
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bloquear cartão</DialogTitle>
          <DialogDescription>
            Bloquear este cartão impede o operador de iniciar o processo até que o motivo seja resolvido. Confirmar bloqueio?
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="motivo">Motivo</Label>
            <Select 
              value={tipoInterrupcaoId} 
              onValueChange={(val) => {
                setTipoInterrupcaoId(val);
                if (val !== 'OUTRO') setOutro('');
                setErro(null);
              }}
              disabled={carregandoMotivos || loadingBloquear}
            >
              <SelectTrigger id="motivo">
                <SelectValue placeholder={carregandoMotivos ? "Carregando..." : "Selecione um motivo"} />
              </SelectTrigger>
              <SelectContent>
                {motivos.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                ))}
                <SelectItem value="OUTRO">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {tipoInterrupcaoId === 'OUTRO' && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="outro">Descreva o motivo</Label>
              <Textarea 
                id="outro" 
                placeholder="Descreva o motivo (obrigatório)" 
                value={outro}
                onChange={(e) => {
                  setOutro(e.target.value);
                  setErro(null);
                }}
                disabled={loadingBloquear}
              />
            </div>
          )}

          {erro && (
            <p className="text-[13px] font-medium text-rose-600">{erro}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loadingBloquear}>
            Cancelar
          </Button>
          <Button 
            className="bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90" 
            onClick={() => void handleBloquear()} 
            disabled={loadingBloquear}
          >
            {loadingBloquear && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Confirmar bloqueio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
